import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { makeSelectUser } from '@webapp/core/stores/global/selectors'
import { mapLegalEntitySummaryWithBaseCurrencyApiModel } from '@webapp/platform/legal-entities/data-access/mappers/mapLegalEntitySummaryWithBaseCurrencyApiModel'
import { LegalEntitySummaryWithBaseCurrency } from '@webapp/platform/legal-entities/entities/LegalEntitySummaryWithBaseCurrency'

interface IUseQueryUserLegalEntitiesReturn {
  legalEntities: LegalEntitySummaryWithBaseCurrency[]
  defaultLegalEntity: Nullable<LegalEntitySummaryWithBaseCurrency>
}

/**
 * Store-sourced query model: the user's assigned Legal Entities arrive on
 * `/global` at bootstrap, so there is no repository or network call.
 */
export const useQueryUserLegalEntities =
  (): IUseQueryUserLegalEntitiesReturn => {
    const user = useSelector(makeSelectUser())
    const assignedLegalEntities = user?.legal_entities
    const homeLegalEntityUuid = user?.department?.branch?.legal_entity?.uuid

    const legalEntities = useMemo(
      () =>
        (assignedLegalEntities ?? [])
          .map(
            (legalEntity) =>
              new LegalEntitySummaryWithBaseCurrency(
                mapLegalEntitySummaryWithBaseCurrencyApiModel(legalEntity)
              )
          )
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
          ),
      [assignedLegalEntities]
    )

    const defaultLegalEntity = useMemo(
      () =>
        legalEntities.find(
          (legalEntity) => legalEntity.uuid === homeLegalEntityUuid
        ) ??
        legalEntities[0] ??
        null,
      [legalEntities, homeLegalEntityUuid]
    )

    return { legalEntities, defaultLegalEntity }
  }
